import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Trash2, Plus, ArrowLeft } from "lucide-react";
import { toast } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { loyaltyLinks } from "@/components/lib/SidebarLinks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import FormInputField from "@/components/lib/from-input-field";
import CreateItem from "@/components/dialog/menu-manage/common-dialog/create-item";
import LoyaltyCreateSkeleton from "@/components/loyalty/LoyaltyCreateSkeleton";

import useAuth from "@/hooks/useAuth";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import {
  addBuyGetItemOfferApi,
  editBuyGetItemOfferApi,
} from "@/api/buyGetItemOffer";
import imageUrl from "@/assets/images/no-img.png";

const offerSchema = z.object({
  name: z.string().min(1, "Offer name is required"),
  description: z.string().optional(),
  status: z.enum(["active", "inactive"]),
});

export default function AddBuyGetItemOffer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updateItem = location.state;
  const { type } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [openBuyModal, setOpenBuyModal] = useState(false);
  const [openGetModal, setOpenGetModal] = useState(false);

  const [buyItems, setBuyItems] = useState([]);
  const [getItems, setGetItems] = useState([]);

  const { flatMenus, getLoading: menuLoading } = useSelector(
    (state) => state.menuManage.menu
  );

  const form = useForm({
    resolver: zodResolver(offerSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      status: "active",
    },
  });

  // Fetch flat menus for item picker
  useEffect(() => {
    if (user?.restaurant_id && (!flatMenus || flatMenus.length === 0)) {
      dispatch(fetchAllFlatMenu(user.restaurant_id));
    }
  }, [dispatch, user?.restaurant_id, flatMenus?.length]);

  // Pre-fill form if editing
  useEffect(() => {
    if (updateItem) {
      form.reset({
        name: updateItem.name || "",
        description: updateItem.description || "",
        status: updateItem.status || "active",
      });

      if (updateItem.buy_items) {
        setBuyItems(
          updateItem.buy_items.map((item) => ({
            unique_id: uuidv4(),
            item_id: item.item_id,
            item_type: item.item_type || "drink",
            variant_id: item.variant_id || null,
            item_name: item.menu_item?.name || item.display_name || "",
            variant_name: item.variant?.name || "",
            file_manager_url: item.menu_item?.file_manager_url || "",
            quantity: item.quantity || 1,
          }))
        );
      }

      if (updateItem.get_items) {
        setGetItems(
          updateItem.get_items.map((item) => ({
            unique_id: uuidv4(),
            item_id: item.item_id,
            item_type: item.item_type || "drink",
            variant_id: item.variant_id || null,
            item_name: item.menu_item?.name || item.display_name || "",
            variant_name: item.variant?.name || "",
            file_manager_url: item.menu_item?.file_manager_url || "",
          }))
        );
      }
    }
  }, [updateItem, form]);

  // Item Modal Callbacks
  const handleAddBuyItem = (newItem) => {
    setBuyItems((prev) => [...prev, newItem]);
  };

  const handleRemoveBuyItem = (uniqueId) => {
    setBuyItems((prev) => prev.filter((item) => item.unique_id !== uniqueId));
  };

  const handleAddGetItem = (newItem) => {
    setGetItems((prev) => [...prev, newItem]);
  };

  const handleRemoveGetItem = (uniqueId) => {
    setGetItems((prev) => prev.filter((item) => item.unique_id !== uniqueId));
  };

  // Form Submission
  const handleSubmitOffer = async (data) => {
    if (buyItems.length === 0) {
      toast.error("Please add at least one Buy item.");
      return;
    }

    if (getItems.length === 0) {
      toast.error("Please add at least one Get Free item.");
      return;
    }

    setLoading(true);

    const payload = {
      client_id: user?.client_id || 1,
      restaurant_id: user?.restaurant_id || 1,
      name: data.name,
      description: data.description || "",
      status: data.status,
      offer_buy_items: buyItems.map((item) => ({
        item_type: item.item_type || "drink",
        item_id: item.item_id,
        variant_id: item.variant_id || null,
        quantity: item.quantity || 1,
      })),
      offer_get_items: getItems.map((item) => ({
        item_type: item.item_type || "drink",
        item_id: item.item_id,
        variant_id: item.variant_id || null,
      })),
    };

    try {
      let res;
      if (updateItem && updateItem.id) {
        res = await editBuyGetItemOfferApi(payload, updateItem.id);
      } else {
        res = await addBuyGetItemOfferApi(payload);
      }

      if (res.status === "success") {
        toast.success(
          updateItem
            ? "Offer updated successfully!"
            : "Offer created successfully!"
        );
        navigate("/offer/buy-get-item");
      } else {
        toast.error(res.message || "Failed to save offer.");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (menuLoading) return <LoyaltyCreateSkeleton links={loyaltyLinks} />;

  return (
    <Layout2
      pageName={{ title: updateItem ? "Update Offer" : "Add Buy & Get Offer" }}
      paths={[
        { name: "Loyalty", path: "/loyalty" },
        { name: "Offers", path: "/offer/buy-get-item" },
        { name: "Buy & Get Item", path: "/offer/buy-get-item" },
        { name: updateItem ? "Update Offer" : "Add New Offer" },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={loyaltyLinks} />}
    >
      <div className="w-full max-w-5xl mx-auto my-5 mb-10 px-4">
        <Card className="bg-cblack4 text-cwhite border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-cwhite text-xl">
              {updateItem ? "Update Buy & Get Item Offer" : "Create Buy & Get Item Offer"}
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate("/offer/buy-get-item")}
              className="border-neutral-700 text-cwhite hover:bg-cblack3"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </CardHeader>
          <Separator className="bg-neutral-800" />
          <CardContent className="mt-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmitOffer)}
                className="flex flex-col gap-6"
              >
                {/* Offer Name */}
                <FormInputField
                  name="name"
                  style="bg-cblack3 border-neutral-700"
                  placeholder="Enter offer name (e.g. Buy a Large Coke, Get Small Coke Free)"
                  form={form}
                  type="text"
                  label="Offer Name"
                  required={true}
                />

                {/* Description */}
                <FormInputField
                  name="description"
                  style="bg-cblack3 border-neutral-700"
                  placeholder="Enter offer description"
                  form={form}
                  type="text"
                  label="Description"
                />

                {/* Status */}
                <FormInputField
                  name="status"
                  style="bg-cblack3 border-neutral-700"
                  placeholder="Select status"
                  form={form}
                  type="select"
                  selectOption={[
                    { label: "Active", value: "active" },
                    { label: "Inactive", value: "inactive" },
                  ]}
                  label="Status"
                  required={true}
                />

                <Separator className="bg-neutral-800 my-2" />

                {/* SECTION 1: BUY ITEMS */}
                <div className="border border-neutral-800 p-5 rounded-lg bg-cblack3">
                  <div className="flex justify-between items-center gap-4 mb-4">
                    <div>
                      <h2 className="font-bold text-lg text-cwhite">Buy Items (Required to Buy)</h2>
                      <p className="text-xs text-gray-400">
                        Add menu items customers must purchase to qualify for this offer
                      </p>
                    </div>
                    <Button
                      type="button"
                      className={`${
                        buyItems && buyItems.length >= 1
                          ? "opacity-50 cursor-not-allowed bg-neutral-700 hover:bg-neutral-700 text-neutral-400"
                          : "bg-cyellow text-black font-semibold hover:bg-yellow-500"
                      }`}
                      onClick={() => {
                        if (buyItems && buyItems.length >= 1) {
                          toast.error("Only one Buy item is allowed.");
                        } else {
                          setOpenBuyModal(true);
                        }
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Buy Item
                    </Button>
                  </div>

                  {buyItems && buyItems.length > 0 ? (
                    <Table className="mt-2 border border-neutral-800 rounded">
                      <TableHeader className="bg-cblack4">
                        <TableRow className="border-neutral-800">
                          <TableHead className="text-gray-300">Item Name</TableHead>
                          <TableHead className="text-gray-300">Image</TableHead>
                          <TableHead className="text-center text-gray-300">Quantity</TableHead>
                          <TableHead className="text-center text-gray-300">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {buyItems.map((item) => (
                          <TableRow key={item.unique_id} className="border-neutral-800">
                            <TableCell className="font-medium text-cwhite">
                              <span>{item.item_name}</span>
                              {item.variant_name && (
                                <span className="text-xs text-cyellow pl-1">
                                  ({item.variant_name})
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <img
                                src={item.file_manager_url || imageUrl}
                                alt={item.item_name || "Item"}
                                className="w-12 h-12 rounded object-cover border border-neutral-700"
                              />
                            </TableCell>
                            <TableCell className="text-center text-cwhite font-semibold">
                              {item.quantity || 1}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-cred hover:bg-red-900/20 hover:text-red-400"
                                onClick={() => handleRemoveBuyItem(item.unique_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-6 border border-dashed border-neutral-700 rounded text-sm text-gray-500">
                      No Buy items added yet. Click "Add Buy Item" above.
                    </div>
                  )}
                </div>

                {/* SECTION 2: GET ITEMS */}
                <div className="border border-neutral-800 p-5 rounded-lg bg-cblack3">
                  <div className="flex justify-between items-center gap-4 mb-4">
                    <div>
                      <h2 className="font-bold text-lg text-cwhite">Get Items (Reward Free Items)</h2>
                      <p className="text-xs text-gray-400">
                        Add menu items customers receive for free when qualifying
                      </p>
                    </div>
                    <Button
                      type="button"
                      className={`${
                        getItems && getItems.length >= 1
                          ? "opacity-50 cursor-not-allowed bg-neutral-700 hover:bg-neutral-700 text-neutral-400"
                          : "bg-cyellow text-black font-semibold hover:bg-yellow-500"
                      }`}
                      onClick={() => {
                        if (getItems && getItems.length >= 1) {
                          toast.error("Only one Reward item is allowed.");
                        } else {
                          setOpenGetModal(true);
                        }
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Reward Item
                    </Button>
                  </div>

                  {getItems && getItems.length > 0 ? (
                    <Table className="mt-2 border border-neutral-800 rounded">
                      <TableHeader className="bg-cblack4">
                        <TableRow className="border-neutral-800">
                          <TableHead className="text-gray-300">Item Name</TableHead>
                          <TableHead className="text-gray-300">Image</TableHead>
                          <TableHead className="text-center text-gray-300">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getItems.map((item) => (
                          <TableRow key={item.unique_id} className="border-neutral-800">
                            <TableCell className="font-medium text-cwhite">
                              <span>{item.item_name}</span>
                              {item.variant_name && (
                                <span className="text-xs text-cyellow pl-1">
                                  ({item.variant_name})
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <img
                                src={item.file_manager_url || imageUrl}
                                alt={item.item_name || "Item"}
                                className="w-12 h-12 rounded object-cover border border-neutral-700"
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-cred hover:bg-red-900/20 hover:text-red-400"
                                onClick={() => handleRemoveGetItem(item.unique_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-6 border border-dashed border-neutral-700 rounded text-sm text-gray-500">
                      No Reward items added yet. Click "Add Reward Item" above.
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/offer/buy-get-item")}
                    className="border-neutral-700 text-cwhite hover:bg-cblack3"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-cyellow text-black hover:bg-yellow-500 font-bold px-8"
                  >
                    {loading
                      ? "Saving..."
                      : updateItem
                      ? "Update Offer"
                      : "Create Offer"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* Item Picker Modals */}
      <CreateItem
        isOpen={openBuyModal}
        onClose={() => setOpenBuyModal(false)}
        title="Add Buy Item"
        quantity={true}
        onSuccess={handleAddBuyItem}
      />

      <CreateItem
        isOpen={openGetModal}
        onClose={() => setOpenGetModal(false)}
        title="Add Reward Item"
        quantity={false}
        onSuccess={handleAddGetItem}
      />
    </Layout2>
  );
}
